/**
 * CoachEvaluations — Cuadro de evaluación de entrenadores (admin only).
 * 3 dominios ponderados: Entrenamiento (40%) · Partido (30%) · Perfil (30%).
 * Escala 1-5. Nota final ponderada + clasificación automática.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ClipboardCheck, Save, FileDown, ChevronLeft, User, Briefcase,
} from "lucide-react";
import { toast } from "sonner";

const SCALE = [
  { value: 5, label: "Excelente",     cls: "bg-emerald-100 text-emerald-700 border-emerald-300 ring-emerald-500" },
  { value: 4, label: "Bueno",         cls: "bg-blue-100 text-blue-700 border-blue-300 ring-blue-500" },
  { value: 3, label: "Adecuado",      cls: "bg-cyan-100 text-cyan-700 border-cyan-300 ring-cyan-500" },
  { value: 2, label: "Mejorable",     cls: "bg-amber-100 text-amber-700 border-amber-300 ring-amber-500" },
  { value: 1, label: "Muy mejorable", cls: "bg-rose-100 text-rose-700 border-rose-300 ring-rose-500" },
];

const CLASS_TONE = {
  "Excelente":      "text-emerald-700 bg-emerald-50 border-emerald-300",
  "Muy Bueno":      "text-blue-700 bg-blue-50 border-blue-300",
  "Bueno":          "text-cyan-700 bg-cyan-50 border-cyan-300",
  "Adecuado":       "text-amber-700 bg-amber-50 border-amber-300",
  "Plan de mejora": "text-rose-700 bg-rose-50 border-rose-300",
};

function currentSeasonId() {
  const d = new Date();
  const y = d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1;
  return `${String(y).slice(2)}-${String(y + 1).slice(2)}`;
}

function classify(note) {
  if (note >= 4.5) return "Excelente";
  if (note >= 4.0) return "Muy Bueno";
  if (note >= 3.5) return "Bueno";
  if (note >= 3.0) return "Adecuado";
  return "Plan de mejora";
}

export default function CoachEvaluations() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [coaches, setCoaches] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    if (!authLoading && user && user.role !== "admin") {
      toast.error("Sección solo para el Director de Metodología.");
      navigate("/dashboard", { replace: true });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    let cancel = false;
    Promise.all([
      api.get("/coach-evaluations/coaches"),
      api.get("/coach-evaluations/catalog"),
    ])
      .then(([r1, r2]) => {
        if (cancel) return;
        setCoaches(r1.data || []);
        setCatalog(r2.data || null);
      })
      .catch((e) => !cancel && toast.error(formatApiError(e)));
    return () => { cancel = true; };
  }, []);

  if (!catalog) {
    return <div className="text-sm text-slate-400 py-10 text-center italic">Cargando…</div>;
  }

  return (
    <div className="space-y-6 pb-24">
      <div>
        <h1 className="text-3xl font-black tracking-tight text-slate-900 flex items-center gap-2"
            style={{ fontFamily: "Nunito" }}>
          <ClipboardCheck className="h-7 w-7 text-purple-600" /> Evaluación de entrenadores
        </h1>
        <p className="text-sm text-slate-500">
          Dirección de Metodología · Entrenamiento (40%) · Partido (30%) · Perfil (30%) · Escala 1-5
        </p>
      </div>

      {!selectedCoach ? (
        <CoachGrid coaches={coaches} onSelect={setSelectedCoach} />
      ) : (
        <CoachEvaluationForm
          coach={selectedCoach}
          catalog={catalog}
          onBack={() => setSelectedCoach(null)}
        />
      )}
    </div>
  );
}

function CoachGrid({ coaches, onSelect }) {
  if (coaches.length === 0) {
    return <Card><CardContent className="py-10 text-center text-slate-400 italic">
      No hay entrenadores registrados.
    </CardContent></Card>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {coaches.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={() => onSelect(c)}
          className="text-left border border-slate-200 rounded-lg p-4 bg-white hover:border-purple-400 hover:shadow-md transition"
          data-testid={`coach-eval-${c.id}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center">
              <User className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-bold text-slate-800 truncate">{c.name}</div>
              <div className="text-xs text-slate-500 truncate">{c.email}</div>
              <div className="text-[11px] text-purple-700 mt-0.5 truncate">
                {c.teams && c.teams.length > 0
                  ? c.teams.map((t) => `${t.name}${t.is_head ? "" : " (aux.)"}`).join(" · ")
                  : "Sin equipo asignado"}
              </div>
            </div>
            <Briefcase className="h-4 w-4 text-purple-400 ml-auto" />
          </div>
        </button>
      ))}
    </div>
  );
}

function CommitmentIndicator({ coachId, season }) {
  const [agg, setAgg] = useState(null);

  useEffect(() => {
    const startYear = 2000 + parseInt(season.split("-")[0], 10);
    const params = new URLSearchParams({
      date_from: `${startYear}-07-01`,
      date_to: `${startYear + 1}-06-30`,
    });
    api.get(`/coordinator/staff-attendance?${params}`)
      .then((r) => {
        const mine = (r.data.rows || []).filter((row) => row.user_id === coachId);
        const registered = mine.reduce((s, row) => s + row.registered, 0);
        const attended = mine.reduce((s, row) => s + row.attended, 0);
        const trainings = mine.reduce((s, row) => s + row.trainings_total, 0);
        setAgg({
          registered, attended, trainings,
          pct: registered ? Math.round((100 * attended) / registered) : null,
        });
      })
      .catch(() => setAgg({ registered: 0, attended: 0, trainings: 0, pct: null }));
  }, [coachId, season]);

  return (
    <div
      className="flex flex-wrap items-center gap-2 text-xs bg-purple-50 border border-purple-200 rounded-lg px-3 py-2"
      data-testid="coach-commitment-indicator"
    >
      <span className="font-bold text-purple-800 uppercase tracking-widest text-[10px]">
        Compromiso (automático)
      </span>
      <span className="text-slate-600">Asistencia a entrenamientos de sus equipos:</span>
      {agg === null ? (
        <span className="text-slate-400 italic">Cargando…</span>
      ) : agg.pct === null ? (
        <span className="text-slate-400 italic">
          Sin registro de asistencia del staff todavía ({agg.trainings} entrenos en la temporada).
        </span>
      ) : (
        <>
          <span className={`inline-flex px-2 py-0.5 rounded-full font-bold ${
            agg.pct >= 80
              ? "bg-emerald-100 text-emerald-800"
              : agg.pct >= 50
                ? "bg-amber-100 text-amber-700"
                : "bg-red-100 text-red-700"
          }`}>
            {agg.pct}%
          </span>
          <span className="text-slate-500">
            ({agg.attended}/{agg.registered} registrados · {agg.trainings} entrenos)
          </span>
        </>
      )}
    </div>
  );
}

function CoachEvaluationForm({ coach, catalog, onBack }) {
  const [period, setPeriod] = useState("octubre");
  const [season] = useState(currentSeasonId());
  const [form, setForm] = useState({ scores: {}, observations: "", loaded: false });
  const { scores, observations, loaded } = form;
  const [saving, setSaving] = useState(false);
  const reqKey = `${coach.id}::${season}::${period}`;

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get(
          `/coach-evaluations?coach_id=${coach.id}&season=${season}&period=${period}`,
        );
        if (cancel) return;
        const existing = (r.data || [])[0];
        setForm({
          scores: existing?.scores || {},
          observations: existing?.observations || "",
          loaded: true,
        });
      } catch (e) {
        if (!cancel) {
          toast.error(formatApiError(e));
          setForm((f) => ({ ...f, loaded: true }));
        }
      }
    })();
    return () => { cancel = true; };
  }, [coach.id, season, period, reqKey]);

  const setItemScore = (domain, area, idx, value) => {
    setForm((f) => {
      const dMap = { ...(f.scores[domain] || {}) };
      const aMap = { ...(dMap[area] || {}) };
      if (aMap[idx] === value) {
        delete aMap[idx];
      } else {
        aMap[idx] = value;
      }
      dMap[area] = aMap;
      return { ...f, scores: { ...f.scores, [domain]: dMap } };
    });
  };

  // Live summary calculation matching the backend formula
  const summary = useMemo(() => {
    const perDomain = {};
    let weightedSum = 0;
    let weightTotal = 0;
    Object.entries(catalog.catalog).forEach(([domainKey, areas]) => {
      const values = [];
      Object.keys(areas).forEach((areaKey) => {
        const m = scores?.[domainKey]?.[areaKey] || {};
        Object.values(m).forEach((v) => {
          const iv = Number(v);
          if (iv >= 1 && iv <= 5) values.push(iv);
        });
      });
      const avg = values.length ? +(values.reduce((a, b) => a + b, 0) / values.length).toFixed(2) : 0;
      const w = catalog.domain_weights[domainKey] || 0;
      perDomain[domainKey] = { label: catalog.domain_labels[domainKey], average: avg, weight: w, items_rated: values.length };
      if (values.length) { weightedSum += avg * w; weightTotal += w; }
    });
    const finalNote = weightTotal > 0 ? +(weightedSum / weightTotal).toFixed(2) : 0;
    return {
      per_domain: perDomain,
      final_note: finalNote,
      classification: weightTotal > 0 ? classify(finalNote) : "—",
    };
  }, [scores, catalog]);

  const onSave = async () => {
    setSaving(true);
    try {
      await api.post("/coach-evaluations", {
        coach_id: coach.id, season, period,
        scores, observations,
      });
      toast.success("Evaluación guardada");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onDownloadPdf = async () => {
    try {
      if (loaded) await onSave();
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      window.open(
        `${BACKEND}/api/export/coach/${coach.id}/evaluation-pdf?season=${encodeURIComponent(season)}&period=${period}`,
        "_blank", "noopener",
      );
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!loaded) {
    return <div className="text-sm text-slate-400 py-10 text-center italic">Cargando evaluación…</div>;
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="coach-eval-back">
            <ChevronLeft className="h-4 w-4 mr-1" /> Volver
          </Button>
          <div>
            <div className="text-xl font-black text-slate-900 flex items-center gap-2">
              <User className="h-5 w-5 text-purple-600" /> {coach.name}
            </div>
            <div className="text-xs text-slate-500">
              {coach.email}
              {coach.teams && coach.teams.length > 0 && (
                <span className="ml-2 text-purple-700 font-semibold">
                  · {coach.teams.map((t) => t.name).join(", ")}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Periodo</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-48" data-testid="coach-eval-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(catalog.periods || []).map((p) => (
                  <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Temporada</Label>
            <div className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-slate-50 text-slate-700 font-mono w-24 text-center">
              {season}
            </div>
          </div>
          <Button onClick={onSave} disabled={saving}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="coach-eval-save">
            <Save className="h-4 w-4 mr-1.5" /> {saving ? "Guardando…" : "Guardar"}
          </Button>
          <Button onClick={onDownloadPdf} variant="outline" data-testid="coach-eval-pdf">
            <FileDown className="h-4 w-4 mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {/* Scale legend */}
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
        <span className="font-bold text-slate-500 mr-1">Escala:</span>
        {SCALE.map((sc) => (
          <span key={sc.value} className={`px-2 py-1 rounded border ${sc.cls}`}>
            <b>{sc.value}</b> {sc.label}
          </span>
        ))}
      </div>

      <CommitmentIndicator coachId={coach.id} season={season} />

      {/* Summary sticky card */}
      <Card className={`border-2 ${CLASS_TONE[summary.classification] || "border-slate-200"}`}>
        <CardContent className="py-4">
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 items-center">
            {Object.entries(summary.per_domain).map(([k, v]) => (
              <div key={k} className="text-center">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                  {v.label} · {(v.weight * 100).toFixed(0)}%
                </div>
                <div className="text-2xl font-black text-slate-800">{v.average.toFixed(2)}</div>
                <div className="text-[10px] text-slate-400">{v.items_rated} items</div>
              </div>
            ))}
            <div className="text-center sm:col-span-2" data-testid="coach-eval-final">
              <div className="text-[10px] uppercase tracking-widest font-bold text-purple-600">
                Nota final · Clasificación
              </div>
              <div className="text-3xl font-black text-purple-700">{summary.final_note.toFixed(2)}</div>
              <div className={`inline-block mt-1 px-2 py-0.5 rounded border text-xs font-bold ${CLASS_TONE[summary.classification] || "text-slate-600 border-slate-300"}`}>
                {summary.classification}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Domains */}
      {Object.entries(catalog.catalog).map(([domainKey, areas]) => (
        <Card key={domainKey}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              {catalog.domain_labels[domainKey]}
              <span className="text-xs text-slate-400 font-normal">
                ({(catalog.domain_weights[domainKey] * 100).toFixed(0)}%)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.entries(areas).map(([areaKey, area]) => (
              <div key={areaKey} className="mb-4 last:mb-0">
                <div className="font-bold text-sm text-slate-700 mb-2">{area.label}</div>
                <ul className="space-y-1.5">
                  {(area.items || []).map((item, idx) => {
                    const current = scores?.[domainKey]?.[areaKey]?.[idx];
                    return (
                      <li key={idx} className="flex flex-wrap items-center justify-between gap-2 py-1 border-b border-slate-100 last:border-0">
                        <span className="text-sm text-slate-700 flex-1 min-w-[200px]">{item}</span>
                        <div className="flex gap-1">
                          {SCALE.map((sc) => (
                            <button
                              key={sc.value}
                              type="button"
                              onClick={() => setItemScore(domainKey, areaKey, idx, sc.value)}
                              className={`w-9 h-9 rounded-md text-sm font-bold border transition ${
                                current === sc.value
                                  ? `${sc.cls} ring-2`
                                  : "bg-white text-slate-400 border-slate-200 hover:border-slate-400"
                              }`}
                              data-testid={`coach-item-${domainKey}-${areaKey}-${idx}-${sc.value}`}
                              title={sc.label}
                            >
                              {sc.value}
                            </button>
                          ))}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      {/* Observations */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Observaciones del Director de Metodología</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={observations}
            onChange={(e) => setForm((f) => ({ ...f, observations: e.target.value }))}
            placeholder="Comentarios, plan de mejora, hitos del periodo, propuestas formativas…"
            rows={5}
            data-testid="coach-eval-observations"
          />
        </CardContent>
      </Card>

      {/* Sticky save bar */}
      <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-slate-200 -mx-4 px-4 py-3 flex justify-end gap-2">
        <Button onClick={onSave} disabled={saving}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="coach-eval-save-bottom">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? "Guardando…" : "Guardar evaluación"}
        </Button>
        <Button onClick={onDownloadPdf} variant="outline" data-testid="coach-eval-pdf-bottom">
          <FileDown className="h-4 w-4 mr-1.5" /> Descargar PDF
        </Button>
      </div>
    </div>
  );
}
